import { TEST_ID_COMPARE_PLAYER } from "e2e/testId";
import { memo } from "react";

import { Heading, Stack, Text } from "@/components/atoms";
import { ColorClass } from "@/utils/colorScale";
/**
 * Props for the CompositeMetricView component
 */
interface Props {
  formattedValue: string | null;
  label: string;
  styles: ColorClass;
  isSmallScreen: boolean;
  isDark: boolean;
}

/**
 * Renders a single composite metric card with styled value and label.
 * The border is theme-aware via the outline-subtle token (was an isDark
 * zinc-800/surface-muted ternary).
 * @param root0 - The component props
 * @param root0.formattedValue - Formatted metric value to display (null shows empty)
 * @param root0.label - Metric name or description
 * @param root0.styles - Color scheme for border, background, and text
 * @param root0.isSmallScreen - Whether screen width is below mobile breakpoint
 * @param root0.isDark - Unused; retained for the call-site signature
 */
const CompositeMetricView = ({
  formattedValue,
  label,
  styles,
  isSmallScreen: _isSmallScreen,
  isDark: _isDark,
}: Props) => (
  <Stack
    className="min-h-20 flex-1 gap-1 rounded-sm border border-outline-subtle px-3 pb-3 pt-2"
    // eslint-disable-next-line no-restricted-syntax -- data-driven color-scale background (interpolated hex from ColorClass); no closed token
    UNSAFE_style={{ backgroundColor: styles.background }}
  >
    <Heading
      variant="title"
      className="truncate"
      // eslint-disable-next-line no-restricted-syntax -- data-driven color-scale text (interpolated hex from ColorClass); no closed token
      UNSAFE_style={{ color: styles.text }}
    >
      {formattedValue}
    </Heading>
    <Text
      testID={TEST_ID_COMPARE_PLAYER.COMPOSITE_METRIC_ITEM_VALUE}
      variant="caption"
      // eslint-disable-next-line no-restricted-syntax -- data-driven color-scale text (interpolated hex from ColorClass); no closed token
      UNSAFE_style={{ color: styles.text }}
      numberOfLines={2}
      ellipsizeMode="tail"
    >
      {label}
    </Text>
  </Stack>
);

CompositeMetricView.displayName = "CompositeMetricView";

export default memo(CompositeMetricView);
